import { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Spinner } from '../ui';

function SortIcon({ sorted }) {
  if (sorted === 'asc')  return <ChevronUp size={11} className="text-amber" />;
  if (sorted === 'desc') return <ChevronDown size={11} className="text-amber" />;
  return <ChevronsUpDown size={11} className="text-txt-muted opacity-40" />;
}

export default function DataTable({
  columns,
  data = [],
  loading = false,
  pageSize = 20,
  searchable = true,
  searchPlaceholder = 'Search…',
  onRowClick,
  emptyText = 'No records',
  className,
}) {
  const [sorting,        setSorting]        = useState([]);
  const [globalFilter,   setGlobalFilter]   = useState('');
  const [pagination,     setPagination]     = useState({ pageIndex: 0, pageSize });

  const table = useReactTable({
    data: useMemo(() => data, [data]),
    columns,
    state:       { sorting, globalFilter, pagination },
    onSortingChange:     setSorting,
    onGlobalFilterChange:setGlobalFilter,
    onPaginationChange:  setPagination,
    getCoreRowModel:       getCoreRowModel(),
    getSortedRowModel:     getSortedRowModel(),
    getFilteredRowModel:   getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const { rows }        = table.getRowModel();
  const totalPages      = table.getPageCount();
  const currentPage     = table.getState().pagination.pageIndex;
  const filteredTotal   = table.getFilteredRowModel().rows.length;

  if (loading) {
    return <div className="flex justify-center py-12"><Spinner size={20} /></div>;
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Toolbar */}
      {searchable && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-muted" />
            <input
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder={searchPlaceholder}
              className="input-base pl-8 text-xs"
            />
          </div>
          <span className="text-2xs font-mono text-txt-muted">
            {filteredTotal} {filteredTotal === 1 ? 'row' : 'rows'}
          </span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-bg-border">
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'text-left py-2 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest select-none',
                      header.column.getCanSort() && 'cursor-pointer hover:text-txt-secondary'
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{ width: header.column.columnDef.size }}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <SortIcon sorted={header.column.getIsSorted()} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-txt-muted font-mono text-xs">
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn(
                    'border-b border-bg-border/40 transition-colors',
                    onRowClick && 'cursor-pointer hover:bg-bg-hover'
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="py-2.5 px-3 text-txt-secondary">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-2xs font-mono text-txt-muted">
            Page {currentPage + 1} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="btn-ghost p-1.5 disabled:opacity-30"
            >
              <ChevronLeft size={13} />
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="btn-ghost p-1.5 disabled:opacity-30"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
